import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { courseAPI, videoAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { FiMenu, FiX, FiPlay, FiHome, FiInfo } from 'react-icons/fi';
import { getMediaUrl } from '../utils/config';

const Home = () => {
  const { user, logout } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unknownWords, setUnknownWords] = useState([]);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [learningStats, setLearningStats] = useState({
    totalMinutes: 0,
    completedVideos: [],
    consecutiveDays: 0
  });
  
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);

  // 处理触摸开始事件
  const handleTouchStart = (e) => {
    setTouchStartX(e.changedTouches[0].screenX);
  };

  // 处理触摸结束事件
  const handleTouchEnd = (e) => {
    setTouchEndX(e.changedTouches[0].screenX);
    handleSwipe();
  };

  // 处理滑动手势
  const handleSwipe = () => {
    const swipeThreshold = 50;
    if (touchStartX - touchEndX > swipeThreshold) {
      // 向左滑动，隐藏侧边栏
      setSidebarOpen(false);
    } else if (touchEndX - touchStartX > swipeThreshold) {
      // 向右滑动，显示侧边栏
      setSidebarOpen(true);
    }
  };
  const [learnedVideos, setLearnedVideos] = useState([]);
  const navigate = useNavigate();

  // 获取不认识的单词
  const getUnknownWords = () => {
    try {
      const savedWords = localStorage.getItem('unknownWords');
      if (savedWords && savedWords !== 'undefined') {
        setUnknownWords(JSON.parse(savedWords));
      }
    } catch (error) {
      console.error('Failed to get unknown words:', error);
      localStorage.removeItem('unknownWords');
    }
  };

  // 删除单词
  const removeUnknownWord = (wordId) => {
    try {
      const updatedWords = unknownWords.filter(word => word.id !== wordId);
      setUnknownWords(updatedWords);
      localStorage.setItem('unknownWords', JSON.stringify(updatedWords));
    } catch (error) {
      console.error('Failed to remove word:', error);
    }
  };

  // 获取学习统计数据
  const getLearningStats = () => {
    try {
      const savedStats = localStorage.getItem('learningStats');
      console.log('获取学习统计数据:', savedStats);
      if (savedStats) {
        const parsedStats = JSON.parse(savedStats);
        console.log('解析后的统计数据:', parsedStats);
        if (!Array.isArray(parsedStats.completedVideos)) {
          parsedStats.completedVideos = [];
        }
        setLearningStats(parsedStats);
      } else {
        const initialStats = {
          totalMinutes: 0,
          completedVideos: [],
          consecutiveDays: 0,
          lastLearningDate: null,
          learningDays: []
        };
        console.log('初始化统计数据:', initialStats);
        localStorage.setItem('learningStats', JSON.stringify(initialStats));
        setLearningStats(initialStats);
      }
    } catch (error) {
      console.error('Failed to get learning stats:', error);
    }
  };

  // 获取学习过的视频
  const getLearnedVideos = () => {
    try {
      const learnedCourses = JSON.parse((localStorage.getItem('learnedCourses') || '[]').replace(/^"undefined"$/, '[]'));
      console.log('学习过的视频ID:', learnedCourses);
      
      console.log('所有上传的视频:', uploadedVideos);
      
      const videos = uploadedVideos.filter(video => {
        const videoId = video.id;
        const courseId = `uploaded_${video.id}`;
        return learnedCourses.includes(videoId) || learnedCourses.includes(courseId);
      });
      
      console.log('学习过的视频:', videos);
      setLearnedVideos(videos);
    } catch (error) {
      console.error('Failed to get learned videos:', error);
      setLearnedVideos([]);
    }
  };

  // 更新连续学习天数和学习日历
  const updateConsecutiveDays = () => {
    try {
      const savedStats = localStorage.getItem('learningStats');
      if (savedStats) {
        const parsedStats = JSON.parse(savedStats);
        const today = new Date().toDateString();
        const lastDate = parsedStats.lastLearningDate;
        
        if (!parsedStats.learningDays) {
          parsedStats.learningDays = [];
        }
        
        if (!parsedStats.learningDays.includes(today)) {
          parsedStats.learningDays.push(today);
        }
        
        if (lastDate) {
          const lastLearningDate = new Date(lastDate).toDateString();
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const yesterdayString = yesterday.toDateString();
          
          if (lastLearningDate === today) {
            localStorage.setItem('learningStats', JSON.stringify(parsedStats));
            setLearningStats(parsedStats);
            return;
          } else if (lastLearningDate === yesterdayString) {
            parsedStats.consecutiveDays += 1;
          } else {
            parsedStats.consecutiveDays = 1;
          }
        } else {
          parsedStats.consecutiveDays = 1;
        }
        
        parsedStats.lastLearningDate = today;
        localStorage.setItem('learningStats', JSON.stringify(parsedStats));
        setLearningStats(parsedStats);
      }
    } catch (error) {
      console.error('Failed to update consecutive days:', error);
    }
  };

  // 从数据库获取视频列表
  const fetchVideosFromDB = async () => {
    try {
      const res = await videoAPI.getVideos();
      if (res.data.success && res.data.videos) {
        const formattedVideos = res.data.videos.map(video => ({
          id: video.videoId,
          title: video.title,
          description: '',
          category: video.category,
          subcategory: video.subcategory || '',
          level: 'Starters',
          duration: video.duration || 0,
          fileUrl: getMediaUrl(video.videoUrl),
          subtitleUrl: video.subtitleFiles && video.subtitleFiles.length > 0 
            ? getMediaUrl(video.subtitleFiles[0])
            : '',
          posterUrl: video.posterUrl 
            ? getMediaUrl(video.posterUrl)
            : '',
          fileType: 'video',
          hidden: false,
          uploadedAt: video.uploadTime,
          subtitleFiles: video.subtitleFiles || []
        }));
        
        console.log('从数据库获取并转换的视频:', formattedVideos);
        setUploadedVideos(formattedVideos);
        
        localStorage.setItem('uploadedVideos', JSON.stringify(formattedVideos));
        
        getLearnedVideos();
      }
    } catch (error) {
      console.error('从数据库获取视频失败:', error);
      const storedVideos = localStorage.getItem('uploadedVideos');
      if (storedVideos) {
        setUploadedVideos(JSON.parse(storedVideos));
        getLearnedVideos();
      }
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await courseAPI.getCourses();
        setCourses(res.data.courses || []);
      } catch (error) {
        console.error('Failed to fetch courses', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
    fetchVideosFromDB();
    getLearningStats();
    getUnknownWords();
    updateConsecutiveDays();
  }, []);
  
  useEffect(() => {
    if (uploadedVideos.length > 0) {
      getLearnedVideos();
    }
  }, [uploadedVideos]);

  const navigationButtons = [
    {
      id: 1,
      chineseName: '植物学',
      englishName: 'Plants',
      theme: 'plants',
      icon: '🌱',
      emojiCode: '1f331'
    },
    {
      id: 2,
      chineseName: '动物学',
      englishName: 'Animals',
      theme: 'animals',
      icon: '🐅',
      emojiCode: '1f405'
    },
    {
      id: 3,
      chineseName: '人体科学',
      englishName: 'Human Body',
      theme: 'human',
      icon: '🧠',
      emojiCode: '1f9e0'
    },
    {
      id: 4,
      chineseName: '地球科学',
      englishName: 'Earth',
      theme: 'earth',
      icon: '🌍',
      emojiCode: '1f30d'
    },
    {
      id: 5,
      chineseName: '生态学',
      englishName: 'Ecology',
      theme: 'ecology',
      icon: '♻️',
      emojiCode: '267b'
    },
    {
      id: 6,
      chineseName: '宇宙与空间科学',
      englishName: 'Space',
      theme: 'universe',
      icon: '🛰',
      emojiCode: '1f6f0'
    },
    {
      id: 7,
      chineseName: '物理学',
      englishName: 'Physics',
      theme: 'physics',
      icon: '⚡',
      emojiCode: '26a1'
    },
    {
      id: 8,
      chineseName: '化学',
      englishName: 'Chemistry',
      theme: 'chemistry',
      icon: '🧪',
      emojiCode: '1f9ea'
    },
    {
      id: 9,
      chineseName: '基础生物学',
      englishName: 'Biology',
      theme: 'biology',
      icon: '🧬',
      emojiCode: '1f9ec'
    },
    {
      id: 10,
      chineseName: '微生物学',
      englishName: 'Microbes',
      theme: 'microbes',
      icon: '🦠',
      emojiCode: '1f9a0'
    }
  ];

  return (
    <div className="home" style={{ height: '100vh', backgroundColor: '#ffffff', color: '#1a2b4c', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部导航栏 */}
      <nav style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0,
        right: 0,
        zIndex: 1000, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        height: '64px',
        minHeight: '64px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center'
      }}>
        <div className="container" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          padding: '0 1rem',
          boxSizing: 'border-box',
          height: '100%'
        }}>
          {/* 左侧区域 - 汉堡菜单 + Logo */}
          <div className="nav-left-area" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* 汉堡菜单按钮 - 仅在小屏幕显示 */}
            <button 
              className="hamburger-menu-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                display: 'none',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.75rem',
                flexShrink: 0,
                color: '#1a2b4c',
                padding: '0.5rem 0.7rem',
                lineHeight: '1'
              }}
            >
              ☰
            </button>
          
            {/* Logo - 左侧 */}
            <Link to="/" style={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              color: '#1a2b4c',
              minWidth: 0,
              flexShrink: 1
            }}>
              <img 
                src="/logo.png" 
                alt="123wonder英文科普" 
                style={{ 
                  height: 'clamp(40px, 6vw, 60px)', 
                  width: 'auto',
                  maxWidth: '100%'
                }} 
              />
            </Link>
          </div>
          
          {/* 右侧按钮 - 大屏幕显示，小屏幕隐藏 */}
          <div className="navbar-right-buttons" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
            {/* 视频库按钮 */}
            <Link 
              to="/video-library"
              style={{ 
                color: '#000000', 
                textDecoration: 'none',
                fontWeight: '700',
                padding: '0.6rem 1.4rem',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                backgroundColor: '#f0f8ff',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e6f3ff';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.25)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f0f8ff';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.15)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🎬 看动画
            </Link>
            
            {/* 音频库按钮 */}
            <Link 
              to="/audio-library"
              style={{ 
                color: '#000000', 
                textDecoration: 'none',
                fontWeight: '700',
                padding: '0.6rem 1.4rem',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                backgroundColor: '#f0f8ff',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e6f3ff';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.25)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f0f8ff';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.15)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🎵 听播客
            </Link>
            

            
            {/* 英语卡片按钮 */}
            <Link 
              to="/english-cards"
              className="english-cards-button"
              style={{ 
                color: '#000000', 
                textDecoration: 'none',
                fontWeight: '700',
                padding: '0.6rem 1.4rem',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                backgroundColor: '#f0f8ff',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#e6f3ff';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 102, 255, 0.25)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f0f8ff';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.15)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🃏 记单词
            </Link>
            
            {/* 欢迎语 */}
            <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: '500', marginLeft: '0.6rem' }}>
              欢迎，用户{user?.phone ? user.phone.slice(-4) : '0000'}
            </span>
            
            {/* 登出按钮 - 可爱风格 */}
            <button
              onClick={logout}
              style={{ 
                color: '#000000', 
                textDecoration: 'none',
                fontWeight: '700',
                padding: '0.6rem 1.4rem',
                borderRadius: '16px',
                transition: 'all 0.3s ease',
                backgroundColor: '#ffe6e6',
                border: '2px solid rgba(255, 102, 102, 0.4)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(255, 102, 102, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#ffcccc';
                e.target.style.boxShadow = '0 6px 16px rgba(255, 102, 102, 0.25)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#ffe6e6';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 102, 102, 0.15)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              👋 登出
            </button>
          </div>
        </div>
      </nav>

      {/* 主要内容区 */}
      <section style={{
        flex: 1,
        overflowY: 'auto',
        padding: '60px 0 2rem 0',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="container" style={{
          maxWidth: '1600px',
          margin: '0 auto',
          padding: '1.5rem 1.5rem 0 1.5rem'
        }}>

          
          <div 
            className="home-content-wrapper"
            style={{
              display: 'flex',
              gap: '1.25rem',
              alignItems: 'flex-start',
              position: 'relative'
            }}
          >
            {/* 遮罩层 - 点击关闭侧边栏 */}
          <div 
            className={`sidebar-overlay ${sidebarOpen ? 'sidebar-open' : ''}`}
            onClick={() => setSidebarOpen(false)}
            style={{
              display: 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 998,
              pointerEvents: sidebarOpen ? 'auto' : 'none'
            }}
          ></div>

          {/* 左侧模块 - 大屏幕显示，小屏幕隐藏，点击汉堡菜单时显示 */}
          <div 
            className={`home-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
            style={{
              width: '300px',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              flexShrink: 0,
              marginRight: '1rem',
              zIndex: 999,
              pointerEvents: 'auto',
              position: 'relative'
            }}>
              {/* 用户信息头部 */}
              <div className="sidebar-user-info" style={{
                backgroundColor: '#f0f8ff',
                borderRadius: '20px',
                padding: '1rem',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)',
                display: 'none'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #b3d9ff, #80b3ff)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      fontWeight: '600'
                    }}>
                      👤
                    </div>
                    <div>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1a2b4c',
                        marginBottom: '2px'
                      }}>
                        用户{user?.phone ? user.phone.slice(-4) : '0000'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    style={{
                      padding: '0.4rem 0.8rem',
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      color: '#666',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#ff4757';
                      e.target.style.color = 'white';
                      e.target.style.borderColor = '#ff4757';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#666';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    退出
                  </button>
                </div>
              </div>

              {/* 学习统计模块 */}
              <div style={{
                backgroundColor: '#f0f8ff',
                borderRadius: '20px',
                padding: '1.5rem 1.25rem',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)'
              }}>
                <h3 style={{
                  color: '#000000',
                  fontSize: '1.15rem',
                  fontWeight: '800',
                  margin: '0 0 1.25rem 0',
                  textAlign: 'center'
                }}>学习统计</h3>
                <div style={{
                  display: 'flex',
                  gap: '1.25rem',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Link to="/learning-statistics?status=all" style={{ textAlign: 'center', flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '800',
                      color: '#000000',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }} onMouseEnter={(e) => { e.target.style.transform = 'scale(1.1)'; }} onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}>{uploadedVideos.length || 0}</div>
                    <div style={{ fontSize: '0.85rem', color: '#000000', fontWeight: '600' }}>总数</div>
                  </Link>
                  
                  <Link to="/learning-statistics?status=learned" style={{ textAlign: 'center', flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '800',
                      color: '#4caf50',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }} onMouseEnter={(e) => { e.target.style.transform = 'scale(1.1)'; }} onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}>{(() => {
                      const learnedCourses = JSON.parse((localStorage.getItem('learnedCourses') || '[]').replace(/^"undefined"$/, '[]'));
                      const uniqueLearnedVideos = new Set();
                      uploadedVideos.forEach(video => {
                        const videoId = video.id;
                        const courseId = `uploaded_${video.id}`;
                        if (learnedCourses.includes(videoId) || learnedCourses.includes(courseId)) {
                          uniqueLearnedVideos.add(videoId);
                        }
                      });
                      return uniqueLearnedVideos.size || 0;
                    })()}</div>
                    <div style={{ fontSize: '0.85rem', color: '#000000', fontWeight: '600' }}>已学</div>
                  </Link>
                  
                  <Link to="/learning-statistics?status=unlearned" style={{ textAlign: 'center', flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '800',
                      color: '#0066ff',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }} onMouseEnter={(e) => { e.target.style.transform = 'scale(1.1)'; }} onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}>{(() => {
                      const learnedCourses = JSON.parse((localStorage.getItem('learnedCourses') || '[]').replace(/^"undefined"$/, '[]'));
                      const uniqueLearnedVideos = new Set();
                      uploadedVideos.forEach(video => {
                        const videoId = video.id;
                        const courseId = `uploaded_${video.id}`;
                        if (learnedCourses.includes(videoId) || learnedCourses.includes(courseId)) {
                          uniqueLearnedVideos.add(videoId);
                        }
                      });
                      return Math.max(0, uploadedVideos.length - uniqueLearnedVideos.size);
                    })()}</div>
                    <div style={{ fontSize: '0.85rem', color: '#000000', fontWeight: '600' }}>未学</div>
                  </Link>
                  
                  <Link to="/learning-statistics?status=collected" style={{ textAlign: 'center', flex: 1, textDecoration: 'none' }}>
                    <div style={{
                      fontSize: '2rem',
                      fontWeight: '800',
                      color: '#ff9800',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease'
                    }} onMouseEnter={(e) => { e.target.style.transform = 'scale(1.1)'; }} onMouseLeave={(e) => { e.target.style.transform = 'scale(1)'; }}>{(() => {
                      const collectedVideos = JSON.parse((localStorage.getItem('collectedVideos') || '[]').replace(/^"undefined"$/, '[]'));
                      return collectedVideos.length || 0;
                    })()}</div>
                    <div style={{ fontSize: '0.85rem', color: '#000000', fontWeight: '600' }}>收藏</div>
                  </Link>
                </div>
              </div>

              {/* 导航链接 */}
              <div className="sidebar-navigation" style={{
                backgroundColor: '#f0f8ff',
                borderRadius: '20px',
                padding: '1rem',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)'
              }}>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
                  <Link 
                    to="/video-library"
                    className="sidebar-nav-button"
                    style={{ 
                      color: '#0066ff', 
                      textDecoration: 'none',
                      fontWeight: '600',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      backgroundColor: 'white',
                      border: '2px solid rgba(0, 102, 255, 0.3)',
                      fontSize: '1rem',
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0, 102, 255, 0.15)',
                      flex: 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    🎬 看动画
                  </Link>
                  
                  <Link 
                    to="/audio-library"
                    className="sidebar-nav-button"
                    style={{ 
                      color: '#0066ff', 
                      textDecoration: 'none',
                      fontWeight: '600',
                      padding: '0.8rem 1rem',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      backgroundColor: 'white',
                      border: '2px solid rgba(0, 102, 255, 0.3)',
                      fontSize: '1rem',
                      textAlign: 'center',
                      boxShadow: '0 2px 8px rgba(0, 102, 255, 0.15)',
                      flex: 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    🎵 听播客
                  </Link>
                </div>
              </div>

              {/* 日历模块 */}
              <div style={{
                backgroundColor: '#f0f8ff',
                borderRadius: '20px',
                padding: '1.25rem 1.25rem 0.75rem 1.25rem',
                border: '2px solid rgba(0, 102, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 102, 255, 0.15)'
              }}>
                {/* 日历头部 - 月份年份导航 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                  padding: '0 0.25rem'
                }}>
                  <button
                    onClick={() => {
                      let newMonth = currentMonth - 1;
                      let newYear = currentYear;
                      if (newMonth < 0) {
                        newMonth = 11;
                        newYear -= 1;
                      }
                      setCurrentMonth(newMonth);
                      setCurrentYear(newYear);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      border: '2px solid rgba(0, 102, 255, 0.3)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0, 102, 255, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e6f3ff';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 102, 255, 0.1)';
                    }}
                  >
                    ←
                  </button>
                  <h3 style={{
                    color: '#000000',
                    fontSize: '1.1rem',
                    fontWeight: '800',
                    margin: 0,
                    textAlign: 'center',
                    flex: 1
                  }}>
                    {(() => {
                      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                      return `${months[currentMonth]} ${currentYear}`;
                    })()}
                  </h3>
                  <button
                    onClick={() => {
                      let newMonth = currentMonth + 1;
                      let newYear = currentYear;
                      if (newMonth > 11) {
                        newMonth = 0;
                        newYear += 1;
                      }
                      setCurrentMonth(newMonth);
                      setCurrentYear(newYear);
                    }}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      border: '2px solid rgba(0, 102, 255, 0.3)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0, 102, 255, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e6f3ff';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 102, 255, 0.1)';
                    }}
                  >
                    →
                  </button>
                </div>

                {/* 星期标题 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '0.4rem',
                  textAlign: 'center',
                  marginBottom: '0.75rem',
                  padding: '0 0.25rem'
                }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <div key={index} style={{
                      color: '#000000',
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '24px'
                    }}>{day}</div>
                  ))}
                </div>

                {/* 日历日期 */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '0.4rem',
                  textAlign: 'center',
                  padding: '0 0.25rem'
                }}>
                  {(() => {
                    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
                    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                    
                    const savedStats = localStorage.getItem('learningStats');
                    const stats = savedStats ? JSON.parse(savedStats) : { learningDays: [] };
                    const learningDays = stats.learningDays || [];
                    
                    const calendarDays = [];
                    
                    for (let i = 0; i < firstDay; i++) {
                      calendarDays.push(null);
                    }
                    
                    const today = new Date().toDateString();
                    for (let day = 1; day <= daysInMonth; day++) {
                      const dateStr = new Date(currentYear, currentMonth, day).toDateString();
                      const hasLearned = learningDays.includes(dateStr);
                      const isToday = dateStr === today;
                      calendarDays.push({ day, hasLearned, dateStr, isToday });
                    }
                    
                    return calendarDays.map((dayData, index) => {
                      if (!dayData) {
                        return <div key={index} style={{ height: '32px' }}></div>;
                      }
                      
                      return (
                        <div 
                        key={index} 
                        style={{
                          aspectRatio: '1/1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          fontSize: '0.85rem',
                          color: dayData.isToday ? '#ffffff' : (dayData.hasLearned ? '#000000' : '#666666'),
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          backgroundColor: dayData.isToday ? '#0066ff' : (dayData.hasLearned ? '#C7EDCC' : 'transparent'),
                          border: 'none',
                          boxSizing: 'border-box',
                          fontWeight: dayData.isToday || dayData.hasLearned ? '700' : '500'
                        }}
                      >
                        {dayData.day}
                      </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* 理念语文本框 */}
              <div style={{
                backgroundColor: 'rgba(240, 248, 255, 0.6)',
                borderRadius: '20px',
                padding: '0.75rem 1.25rem',
                marginTop: '0.25rem',
                textAlign: 'center'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>👣</span>
                  <span style={{
                    color: '#4a5568',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    fontFamily: '"Comic Sans MS", "PingFang SC", "Microsoft YaHei", cursive, sans-serif',
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap'
                  }}>
                    每天一点点，早早开始，慢慢来。
                  </span>
                  <span style={{ fontSize: '1.2rem' }}>🌱</span>
                </div>
              </div>

            </div>

            {/* 右侧十大主题 */}
            <div style={{ flex: 1 }}>
              {/* 10个导航按钮网格 - 自适应列数 */}
              <div className="home-categories-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '1.25rem',
                marginBottom: '2rem'
              }}>
                {navigationButtons.map((button) => (
                  <Link 
                    key={button.id}
                    to={`/courses?category=${button.theme}`}
                    className="home-category-card"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textDecoration: 'none',
                      backgroundColor: '#f0f8ff',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0, 102, 255, 0.12)',
                      border: '2px solid rgba(0, 102, 255, 0.2)',
                      outline: 'none',
                      padding: '1.5rem 1.25rem'
                    }}
                  >
                    {/* 使用Twemoji统一emoji图片 */}
                    <img 
                      src={`https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/svg/${button.emojiCode}.svg`}
                      alt={button.englishName}
                      style={{
                        width: '120px',
                        height: '120px',
                        marginBottom: '0.75rem',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    {/* 备用emoji */}
                    <div style={{
                      fontSize: '6rem',
                      lineHeight: '1',
                      marginBottom: '0.75rem',
                      display: 'none'
                    }}>
                      {button.icon}
                    </div>
                    
                    {/* 文字区域 */}
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      {/* 英文名称 */}
                      <span style={{
                        color: '#000000',
                        fontSize: '1.2rem',
                        fontWeight: '800',
                        textAlign: 'center',
                        letterSpacing: '-0.3px'
                      }}>
                        {button.englishName}
                      </span>
                      
                      {/* 中文名称 */}
                      <span style={{
                        color: '#000000',
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        textAlign: 'center'
                      }}>
                        {button.chineseName}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 响应式样式 */}
      <style>{`
        /* 侧边栏导航 - 桌面(>1024px)和iPad横屏隐藏 */
        .sidebar-navigation {
          display: none !important;
        }
        
        /* iPad竖屏和手机模式(<=1024px)，侧边栏打开时显示 */
        @media (max-width: 1024px) {
          .home-sidebar.sidebar-open .sidebar-navigation {
            display: flex !important;
            flex-direction: column !important;
          }
        }
        
        /* 确保导航栏内容完美居中 */
        nav {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        nav > .container {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
          display: flex !important;
          align-items: center !important;
          height: 100% !important;
        }
        .nav-left-area {
          display: flex !important;
          align-items: center !important;
          height: 100% !important;
        }
        .hamburger-menu-button {
          display: none !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          padding-top: 0.5rem !important;
          padding-bottom: 0.5rem !important;
        }
        /* 简单直接的Logo垂直居中 */
        @media (max-width: 1024px) {
          .hamburger-menu-button {
            display: flex !important;
          }
          .nav-left-area {
            gap: 0.35rem !important;
          }
          .hamburger-menu-button {
            padding: 0.5rem 0.7rem !important;
            font-size: 1.75rem !important;
          }
          .nav-logo-123, .nav-logo-wonder {
            font-size: 1.6rem !important;
          }
          .nav-category-label {
            font-size: clamp(0.8rem, 2.2vw, 1.4rem) !important;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 40vw;
          }
        }
        @media (max-width: 768px) {
          .nav-left-area {
            gap: 0.25rem !important;
          }
          .hamburger-menu-button {
            padding: 0.5rem 0.7rem !important;
            font-size: 1.75rem !important;
          }
          .nav-logo-123, .nav-logo-wonder {
            font-size: 1.4rem !important;
          }
          .nav-category-label {
            font-size: clamp(0.75rem, 2vw, 1.4rem) !important;
            max-width: 35vw;
          }
        }
        @media (max-width: 480px) {
          .hamburger-menu-button {
            padding: 0.5rem 0.7rem !important;
            font-size: 1.75rem !important;
          }
          .nav-logo-123, .nav-logo-wonder {
            font-size: 1.2rem !important;
          }
          .nav-category-label {
            font-size: clamp(0.7rem, 1.8vw, 1.4rem) !important;
            max-width: 32vw;
          }
        }
        /* 基础网格布局 - 确保卡片不会被挤压 */
        .home-categories-grid {
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 2rem;
        }
        
        /* 大屏幕 - 4列 */
        @media (min-width: 1200px) {
          .home-categories-grid {
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 2.5rem !important;
          }
        }
        
        /* 中等屏幕 - 3列，隐藏英语卡片按钮 */
        @media (min-width: 1025px) and (max-width: 1199px) {
          .home-categories-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 2rem !important;
          }
          
          .container {
            padding: 0 1.5rem;
          }
          
          h1 {
            font-size: 3rem !important;
          }
          
          .sidebar-user-info {
            display: none !important;
          }
          
          .english-cards-button {
            display: none !important;
          }
        }
        
        /* 平板横屏和竖屏(768px-1024px) - 隐藏右侧按钮，显示汉堡菜单 */
        @media (min-width: 768px) and (max-width: 1024px) {
          .english-cards-button {
            display: none !important;
          }
          
          .hamburger-menu-button {
            display: flex !important;
          }
          
          .navbar-right-buttons {
            display: none !important;
          }
          
          .sidebar-overlay {
            display: none !important;
            pointer-events: none !important;
          }
          
          .sidebar-overlay.sidebar-open {
            display: block !important;
            z-index: 998 !important;
            pointer-events: auto !important;
          }
          
          .home-sidebar {
            display: none !important;
            position: fixed !important;
            left: 0;
            top: 60px;
            z-index: 999 !important;
            background-color: #f8f9fa;
            padding: 1rem;
            height: calc(100vh - 60px);
            overflow-y: auto;
            box-shadow: 4px 0 10px rgba(0,0,0,0.1);
            pointer-events: none !important;
          }
          
          .home-sidebar.sidebar-open {
            display: flex !important;
            pointer-events: auto !important;
          }
          
          /* 只有在侧边栏打开时才显示用户信息框 */
          .home-sidebar .sidebar-user-info {
            display: none !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-user-info {
            display: block !important;
          }
          
          /* 只有在侧边栏打开时才显示导航框 */
          .home-sidebar .sidebar-navigation {
            display: none !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-navigation {
            display: block !important;
            z-index: 100001 !important;
            position: relative !important;
            pointer-events: auto !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-navigation div {
            z-index: 100002 !important;
            position: relative !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-navigation a {
            z-index: 100003 !important;
            position: relative !important;
            pointer-events: auto !important;
          }
        }
        
        /* 小屏幕(767px及以下) - 显示汉堡菜单，隐藏右侧按钮 */
        @media (max-width: 767px) {
          .hamburger-menu-button {
            display: flex !important;
          }
          
          .navbar-right-buttons {
            display: none !important;
          }
          
          .sidebar-overlay {
            display: none !important;
          }
          
          .sidebar-overlay.sidebar-open {
            display: block !important;
            z-index: 998 !important;
            pointer-events: auto !important;
          }
          
          .home-sidebar {
            display: none !important;
            position: fixed !important;
            left: 0;
            top: 60px;
            z-index: 999 !important;
            background-color: #f8f9fa;
            padding: 1rem;
            height: calc(100vh - 60px);
            overflow-y: auto;
            box-shadow: 4px 0 10px rgba(0,0,0,0.1);
            pointer-events: none !important;
          }
          
          .home-sidebar.sidebar-open {
            display: flex !important;
            pointer-events: auto !important;
          }
          
          /* 只有在侧边栏打开时才显示用户信息框 */
          .home-sidebar .sidebar-user-info {
            display: none !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-user-info {
            display: block !important;
          }
          
          /* 只有在侧边栏打开时才显示导航框 */
          .home-sidebar .sidebar-navigation {
            display: none !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-navigation {
            display: block !important;
            z-index: 100001 !important;
            position: relative !important;
            pointer-events: auto !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-navigation div {
            z-index: 100002 !important;
            position: relative !important;
          }
          
          .home-sidebar.sidebar-open .sidebar-navigation a {
            z-index: 100003 !important;
            position: relative !important;
            pointer-events: auto !important;
          }
        }
        
        /* 平板竖屏 - 2列 */
        @media (min-width: 600px) and (max-width: 767px) {
          .home-categories-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.75rem !important;
          }
          
          .container {
            padding: 0 1.25rem;
          }
          
          h1 {
            font-size: 2.75rem !important;
          }
        }
        
        /* iPad横屏(768px-1024px) - 3列布局 */
        @media (min-width: 768px) and (max-width: 1024px) {
          .home-categories-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 1.5rem !important;
          }
        }
        
        /* 小屏手机 */
        @media (max-width: 599px) {
          /* 小屏幕时稍微缩小图标，但保持比例 */
          .home-categories-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.5rem !important;
          }
          
          h1 {
            font-size: 2rem !important;
          }
          
          h2 {
            font-size: 1.5rem !important;
          }
          
          section {
            padding: 60px 0 2rem 0 !important;
          }
          
          .container {
            padding: 1rem 1rem 0 1rem !important;
          }
        }
        
        /* 响应式卡片文字大小调整 */
        @media (max-width: 768px) {
          .home-category-card span:first-of-type {
            font-size: 1.1rem !important;
          }
          .home-category-card span:last-of-type {
            font-size: 1rem !important;
          }
        }
        
        /* 侧边栏遮罩层 */
        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.3);
          z-index: 1000;
        }
        
        @media (max-width: 1024px) {
          .sidebar-overlay.sidebar-open {
            display: block;
          }
        }
        
        @keyframes pulse {
          0% {
            opacity: 0.5;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
